import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../../firebase";

// Display only: shows the URL's promo code on the distance step. The real discount is still applied by Stripe at checkout.
// The amount comes from options/validPromoCodes.discounts.{CODE} = { amountOff: 5 } or { percentOff: 10 }.

export function getPromoDiscount(validPromoCodesData, code) {
  if (!code || !validPromoCodesData?.codes || !Object.prototype.hasOwnProperty.call(validPromoCodesData.codes, code)) return null;
  const entry = validPromoCodesData.discounts?.[code] || {};
  const amountOff = Number(entry.amountOff);
  const percentOff = Number(entry.percentOff);
  return {
    code,
    amountOff: amountOff > 0 ? amountOff : null,
    percentOff: !(amountOff > 0) && percentOff > 0 ? percentOff : null,
  };
}

export function describePromoDiscount(discount) {
  if (discount?.amountOff) return `$${formatMoney(discount.amountOff)} off`;
  if (discount?.percentOff) return `${discount.percentOff}% off`;
  return null;
}

// Returns the discounted price as a display string, or null when there's nothing to take off.
export function applyPromoDiscount(price, discount) {
  const amount = Number(`${price ?? ""}`.replace(/[^0-9.]/g, ""));
  if (!discount || !(amount > 0)) return null;
  let result = amount;
  if (discount.amountOff) result = amount - discount.amountOff;
  else if (discount.percentOff) result = amount * (1 - discount.percentOff / 100);
  else return null;
  return formatMoney(Math.max(0, result));
}

function formatMoney(value) {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(2);
}

export function useAppliedPromo(code) {
  const [discount, setDiscount] = useState(null);

  useEffect(() => {
    if (!code) {
      setDiscount(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, "options", "validPromoCodes"))
      .then((snapshot) => {
        if (!cancelled) setDiscount(getPromoDiscount(snapshot.data(), code));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [code]);

  return discount;
}
